import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useEffect, useMemo } from 'react'

import { CacheKeys } from '../lib/cache'
import { isBlank, vinError } from '../lib/sanitization'
import { type PaginatedResults, toCamelCase, toSnakeCase } from '../lib/typeHelper'

export enum EquipmentStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  REMOVED = 'REMOVED',
  PENDING_ADD = 'PENDING_ADD',
  PENDING_CHANGE = 'PENDING_CHANGE',
  PENDING_REMOVE = 'PENDING_REMOVE',
  PENDING_APPROVED = 'PENDING_APPROVED',
  PENDING_REJECTED = 'PENDING_REJECTED',
}

export enum VehicleType {
  TRUCK = 'TRUCK',
  TRACTOR = 'TRACTOR',
  PICKUP = 'PICKUP',
  OTHER = 'OTHER',
  TRAILER = 'TRAILER',
}

export enum VehicleClass {
  TRUCK_TRACTOR = 'TRUCK_TRACTOR',
  STRAIGHT_BOX_TRUCK = 'STRAIGHT_BOX_TRUCK',
  REEFER_TRUCK = 'REEFER_TRUCK',
  PICKUP_TRUCK = 'PICKUP_TRUCK',
  CARGO_VAN = 'CARGO_VAN',
  PASSENGER_VAN = 'PASSENGER_VAN',
  FLATBED = 'FLATBED',
  UTILITY = 'UTILITY',
  DUMP = 'DUMP',
  GARBAGE = 'GARBAGE',
  STAKE = 'STAKE',
  TANK = 'TANK',
  LIVESTOCK = 'LIVESTOCK',
  AGRICULTURAL = 'AGRICULTURAL',
  LOGGING = 'LOGGING',
  TILT = 'TILT',
  FRONT_LOADER = 'FRONT_LOADER',
  AUTO_HAULER = 'AUTO_HAULER',
  OTHER = 'OTHER',

  // Trailer specific classes
  DRY_VAN = 'DRY_VAN',
  REEFER = 'REEFER',
  LOWBOY = 'LOWBOY',
  BULK_COMMODITY = 'BULK_COMMODITY',
  POLE = 'POLE',
  RAG_TOP = 'RAG_TOP',
}

export enum WeightClass {
  UNDER_3000 = 'A',
  FROM_3001_TO_3500 = 'B',
  FROM_3501_TO_4000 = 'C',
  FROM_4001_TO_4500 = 'D',
  FROM_4501_TO_5000 = 'E',
  FROM_5001_TO_5500 = 'F',
  FROM_5501_TO_6000 = 'G',
  FROM_6001_TO_10000 = 'H',
  FROM_10001_TO_14000 = '3',
  FROM_14001_TO_16000 = '4',
  FROM_16001_TO_19500 = '5',
  FROM_19501_TO_26000 = '6',
  FROM_26001_TO_33000 = '7',
  OVER_33000 = '8',
}

export const VehicleClassLabels: Record<VehicleClass, string> = {
  [VehicleClass.TRUCK_TRACTOR]: 'Truck Tractor',
  [VehicleClass.STRAIGHT_BOX_TRUCK]: 'Straight Box Truck',
  [VehicleClass.REEFER_TRUCK]: 'Reefer Truck',
  [VehicleClass.PICKUP_TRUCK]: 'Pickup Truck',
  [VehicleClass.CARGO_VAN]: 'Cargo Van',
  [VehicleClass.PASSENGER_VAN]: 'Passenger Van',
  [VehicleClass.FLATBED]: 'Flatbed',
  [VehicleClass.UTILITY]: 'Utility',
  [VehicleClass.DUMP]: 'Dump',
  [VehicleClass.GARBAGE]: 'Garbage',
  [VehicleClass.STAKE]: 'Stake',
  [VehicleClass.TANK]: 'Tank',
  [VehicleClass.LIVESTOCK]: 'Livestock',
  [VehicleClass.AGRICULTURAL]: 'Agricultural',
  [VehicleClass.LOGGING]: 'Logging',
  [VehicleClass.TILT]: 'Tilt',
  [VehicleClass.FRONT_LOADER]: 'Front Loader',
  [VehicleClass.AUTO_HAULER]: 'Auto Hauler',
  [VehicleClass.OTHER]: 'Other',

  [VehicleClass.DRY_VAN]: 'Dry Van',
  [VehicleClass.REEFER]: 'Reefer',
  [VehicleClass.LOWBOY]: 'Lowboy',
  [VehicleClass.BULK_COMMODITY]: 'Bulk Commodity',
  [VehicleClass.POLE]: 'Pole',
  [VehicleClass.RAG_TOP]: 'Rag Top',
}

export const TruckClassLabels = [
  VehicleClass.TRUCK_TRACTOR,
  VehicleClass.STRAIGHT_BOX_TRUCK,
  VehicleClass.REEFER_TRUCK,
  VehicleClass.PICKUP_TRUCK,
  VehicleClass.CARGO_VAN,
  VehicleClass.PASSENGER_VAN,
  VehicleClass.FLATBED,
  // VehicleClass.UTILITY,
  VehicleClass.DUMP,
  VehicleClass.GARBAGE,
  VehicleClass.STAKE,
  VehicleClass.TANK,
  VehicleClass.LIVESTOCK,
  VehicleClass.AGRICULTURAL,
  // VehicleClass.LOGGING,
  // VehicleClass.TILT,
  VehicleClass.AUTO_HAULER,
  // VehicleClass.FRONT_LOADER,
  VehicleClass.OTHER,
]

export const TruckCommonClasses = [
  VehicleClass.TRUCK_TRACTOR,
  VehicleClass.STRAIGHT_BOX_TRUCK,
  VehicleClass.REEFER_TRUCK,
  VehicleClass.PICKUP_TRUCK,
]

export const TrailerClassLabels = [
  VehicleClass.DRY_VAN,
  VehicleClass.REEFER,
  VehicleClass.FLATBED,
  VehicleClass.DUMP,
  VehicleClass.GARBAGE,
  VehicleClass.STAKE,
  VehicleClass.UTILITY,
  VehicleClass.TANK,
  VehicleClass.LIVESTOCK,
  VehicleClass.AGRICULTURAL,
  VehicleClass.LOGGING,
  VehicleClass.TILT,
  VehicleClass.LOWBOY,
  VehicleClass.BULK_COMMODITY,
  VehicleClass.AUTO_HAULER,
  VehicleClass.POLE,
  VehicleClass.RAG_TOP,
  VehicleClass.OTHER,
]

export const TrailerCommonClasses = [VehicleClass.DRY_VAN, VehicleClass.REEFER, VehicleClass.FLATBED]

export const WeightClassLabels: Record<WeightClass, string> = {
  [WeightClass.UNDER_3000]: 'Under 3,000 lbs',
  [WeightClass.FROM_3001_TO_3500]: '3,001 - 3,500 lbs',
  [WeightClass.FROM_3501_TO_4000]: '3,501 - 4,000 lbs',
  [WeightClass.FROM_4001_TO_4500]: '4,001 - 4,500 lbs',
  [WeightClass.FROM_4501_TO_5000]: '4,501 - 5,000 lbs',
  [WeightClass.FROM_5001_TO_5500]: '5,001 - 5,500 lbs',
  [WeightClass.FROM_5501_TO_6000]: '5,501 - 6,000 lbs',
  [WeightClass.FROM_6001_TO_10000]: '6,001 - 10,000 lbs',
  [WeightClass.FROM_10001_TO_14000]: '10,001 - 14,000 lbs',
  [WeightClass.FROM_14001_TO_16000]: '14,001 - 16,000 lbs',
  [WeightClass.FROM_16001_TO_19500]: '16,001 - 19,500 lbs',
  [WeightClass.FROM_19501_TO_26000]: '19,501 - 26,000 lbs',
  [WeightClass.FROM_26001_TO_33000]: '26,001 - 33,000 lbs',
  [WeightClass.OVER_33000]: 'Over 33,000 lbs',
}

export interface PendingEquipmentChange {
  id: string
  status: EquipmentStatus
  vin: string
  make: string
  model: string
  year: number | null
  licensePlate: string
  licenseState: string
  vehicleClass: VehicleClass | null
  weightClass: WeightClass | null
  price: number | null
  datePriceEstimated?: string | null
}

export interface Equipment {
  id: string
  vehicleNumber: string
  telematicsId: string
  vin: string
  make: string
  model: string
  year: number | undefined
  licensePlate: string
  licenseState: string
  status: EquipmentStatus
  dateCreated: string
  dateUpdated: string
  vehicleType?: VehicleType | null
  vehicleClass: VehicleClass | null
  weightClass: WeightClass | null
  isPending?: boolean | null
  price: number | null
  /** Set when price is Cai's estimate rather than a value someone entered. */
  datePriceEstimated?: string | null
  pendingChange?: PendingEquipmentChange | null
}

const OLD_ESTIMATE_MS = 365 * 24 * 60 * 60 * 1000

/** "Estimated" for a saved estimate; it says so once the estimate is over a year old. */
export function priceEstimateLabel(
  datePriceEstimated: string | null | undefined,
  now = new Date()
): string | null {
  if (!datePriceEstimated) return null
  const ageMs = now.getTime() - new Date(datePriceEstimated).getTime()
  return ageMs > OLD_ESTIMATE_MS ? 'Estimated, over a year old' : 'Estimated'
}

export type Truck = Equipment

export type Trailer = Equipment

export const EARLIEST_PLAUSIBLE_MODEL_YEAR = 1980

/** Dealers sell next year's model, so the bound leaves room for one. */
export function latestPlausibleModelYear() {
  return new Date().getFullYear() + 1
}

/**
 * What carriers need that a trailer lacks, in the words its card shows them; mirrors the server's
 * TRAILER_REQUIRED_FIELDS. A VIN no carrier can read is the only hard stop on a filled field: an
 * implausible model year is shown on the confirm step as a warning, so it doesn't count here.
 */
export function missingTrailerFields(trailer: Trailer): string[] {
  return [
    isBlank(trailer.vin) ? 'VIN' : vinError(trailer.vin) && 'a valid VIN',
    isBlank(trailer.make) && 'make',
    isBlank(trailer.model) && 'model',
    isBlank(trailer.year) && 'year',
    isBlank(trailer.vehicleClass) && 'vehicle class',
  ].filter((field): field is string => !!field)
}

/** What carriers need that a truck lacks: a trailer's, plus its weight class. */
export function missingTruckFields(truck: Truck): string[] {
  return [...missingTrailerFields(truck), ...(isBlank(truck.weightClass) ? ['weight class'] : [])]
}

export function isValidTrailer(trailer: Trailer): boolean {
  return missingTrailerFields(trailer).length === 0
}

export function isValidTruck(truck: Truck): boolean {
  return missingTruckFields(truck).length === 0
}

function trucksUrl(orgId: string) {
  return `/api/v1/insure/organisations/${orgId}/trucks/`
}

function trailersUrl(orgId: string) {
  return `/api/v1/insure/organisations/${orgId}/trailers/`
}

function getTrucks(orgId: string, page: number): Promise<PaginatedResults<Truck>> {
  return axios
    .get(trucksUrl(orgId), { params: { page } })
    .then((response) => toCamelCase(response.data) as PaginatedResults<Truck>)
}

function parsePageParam(url: string | null): number | null {
  if (!url) return null
  const params = new URL(url, window.location.origin).searchParams
  const page = params.get('page')
  return page ? Number(page) : null
}

interface UseTrucksProps {
  orgId: string | undefined
  enabled?: boolean
  shouldFetchAllPages?: boolean
}

export function addTruck(orgId: string, data: Partial<Truck>): Promise<Truck> {
  return axios
    .post(trucksUrl(orgId), toSnakeCase(data))
    .then((response) => toCamelCase(response.data) as Truck)
}

export function editTruck(orgId: string, id: string, data: Partial<Truck>): Promise<Truck> {
  return axios
    .patch(`${trucksUrl(orgId)}${id}/`, toSnakeCase(data))
    .then((response) => toCamelCase(response.data) as Truck)
}

export function deleteTruck(orgId: string, id: string): Promise<void> {
  return axios.delete(`${trucksUrl(orgId)}${id}/`)
}

export function fetchRemovedTrucks(orgId: string): Promise<Truck[]> {
  return axios.get(`${trucksUrl(orgId)}removed/`).then((response) => toCamelCase(response.data) as Truck[])
}

export function restoreTruck(orgId: string, id: string): Promise<Truck> {
  return axios
    .post(`${trucksUrl(orgId)}${id}/restore/`)
    .then((response) => toCamelCase(response.data) as Truck)
}

export function approveTruck(orgId: string, id: string): Promise<Truck> {
  return axios
    .post(`${trucksUrl(orgId)}${id}/approve/`)
    .then((response) => toCamelCase(response.data) as Truck)
}

export function rejectTruck(orgId: string, id: string): Promise<Truck> {
  return axios
    .post(`${trucksUrl(orgId)}${id}/reject/`)
    .then((response) => toCamelCase(response.data) as Truck)
}

export type WeightClassesConfig = Record<VehicleClass, WeightClass[]>

function getWeightClassesConfig(): Promise<WeightClassesConfig> {
  return axios
    .get('/api/v1/insure/trucks/weight-classes-config/')
    .then((response) => response.data as WeightClassesConfig)
}

export interface VinDecodeResult {
  make?: string
  model?: string
  year?: number
  vehicleClass?: string
  weightClass?: string
  isTrailer?: boolean
}

export function decodeVin(vin: string): Promise<VinDecodeResult> {
  return axios
    .get('/api/v1/insure/trucks/decode-vin/', { params: { vin } })
    .then((response) => toCamelCase(response.data) as VinDecodeResult)
}

export interface SuggestedValueResult {
  suggestedValue: number | null
}

export function suggestTruckValue(params: {
  make?: string
  model?: string
  year?: number
  vehicleClass?: string
}): Promise<SuggestedValueResult> {
  return axios
    .get('/api/v1/insure/trucks/suggest-value/', { params: toSnakeCase(params) })
    .then((response) => toCamelCase(response.data) as SuggestedValueResult)
}

export function suggestTrailerValue(params: {
  make?: string
  model?: string
  year?: number
  vehicleClass?: string
}): Promise<SuggestedValueResult> {
  return axios
    .get('/api/v1/insure/trailers/suggest-value/', { params: toSnakeCase(params) })
    .then((response) => toCamelCase(response.data) as SuggestedValueResult)
}

export function useWeightClassesConfig() {
  const { data } = useQuery({
    queryKey: [CacheKeys.WEIGHT_CLASSES_CONFIG],
    queryFn: getWeightClassesConfig,
    staleTime: Infinity,
  })
  return data ?? null
}

function getTrailers(orgId: string, page: number): Promise<PaginatedResults<Trailer>> {
  return axios
    .get(trailersUrl(orgId), { params: { page } })
    .then((response) => toCamelCase(response.data) as PaginatedResults<Trailer>)
}

export function addTrailer(orgId: string, data: Partial<Trailer>): Promise<Trailer> {
  return axios
    .post(trailersUrl(orgId), toSnakeCase(data))
    .then((response) => toCamelCase(response.data) as Trailer)
}

export function editTrailer(orgId: string, id: string, data: Partial<Trailer>): Promise<Trailer> {
  return axios
    .patch(`${trailersUrl(orgId)}${id}/`, toSnakeCase(data))
    .then((response) => toCamelCase(response.data) as Trailer)
}

export function deleteTrailer(orgId: string, id: string): Promise<void> {
  return axios.delete(`${trailersUrl(orgId)}${id}/`)
}

export function fetchRemovedTrailers(orgId: string): Promise<Trailer[]> {
  return axios
    .get(`${trailersUrl(orgId)}removed/`)
    .then((response) => toCamelCase(response.data) as Trailer[])
}

export function restoreTrailer(orgId: string, id: string): Promise<Trailer> {
  return axios
    .post(`${trailersUrl(orgId)}${id}/restore/`)
    .then((response) => toCamelCase(response.data) as Trailer)
}

export function approveTrailer(orgId: string, id: string): Promise<Trailer> {
  return axios
    .post(`${trailersUrl(orgId)}${id}/approve/`)
    .then((response) => toCamelCase(response.data) as Trailer)
}

export function rejectTrailer(orgId: string, id: string): Promise<Trailer> {
  return axios
    .post(`${trailersUrl(orgId)}${id}/reject/`)
    .then((response) => toCamelCase(response.data) as Trailer)
}

interface UseTrailersProps {
  orgId: string | undefined
  enabled?: boolean
  shouldFetchAllPages?: boolean
}

/** One truck by id, fetched only when asked for (a details dialog). */
export function useTruck(orgId: string | undefined, id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [CacheKeys.TRUCKS, orgId, id],
    queryFn: () => axios.get(`${trucksUrl(orgId!)}${id}/`).then((r) => toCamelCase(r.data) as Truck),
    enabled: enabled && !!orgId && !!id,
  })
}

/** One trailer by id, fetched only when asked for (a details dialog). */
export function useTrailer(orgId: string | undefined, id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [CacheKeys.TRAILERS, orgId, id],
    queryFn: () => axios.get(`${trailersUrl(orgId!)}${id}/`).then((r) => toCamelCase(r.data) as Trailer),
    enabled: enabled && !!orgId && !!id,
  })
}

export function useTrailers({ orgId, enabled = true, shouldFetchAllPages = false }: UseTrailersProps) {
  const queryEnabled = enabled && !!orgId
  const { data, fetchNextPage, hasNextPage, isFetching, refetch, isLoading } = useInfiniteQuery({
    queryKey: [CacheKeys.TRAILERS, orgId],
    queryFn: ({ pageParam = 1 }) => getTrailers(orgId!, pageParam),
    enabled: queryEnabled,
    refetchOnWindowFocus: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => parsePageParam(lastPage.next),
  })

  const trailers = useMemo(() => data?.pages.flatMap((page) => page.results) ?? [], [data])

  useEffect(() => {
    if (shouldFetchAllPages && !isFetching && hasNextPage && queryEnabled) {
      fetchNextPage()
    }
  }, [shouldFetchAllPages, isFetching, hasNextPage, fetchNextPage, queryEnabled])

  return {
    trailers,
    refetch,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  }
}

export function useTrucks({ orgId, enabled = true, shouldFetchAllPages = false }: UseTrucksProps) {
  const queryEnabled = enabled && !!orgId
  const { data, fetchNextPage, hasNextPage, isFetching, refetch, isLoading } = useInfiniteQuery({
    queryKey: [CacheKeys.TRUCKS, orgId],
    queryFn: ({ pageParam = 1 }) => getTrucks(orgId!, pageParam),
    enabled: queryEnabled,
    refetchOnWindowFocus: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => parsePageParam(lastPage.next),
  })

  const trucks = useMemo(() => data?.pages.flatMap((page) => page.results) ?? [], [data])

  useEffect(() => {
    if (shouldFetchAllPages && !isFetching && hasNextPage && queryEnabled) {
      fetchNextPage()
    }
  }, [shouldFetchAllPages, isFetching, hasNextPage, fetchNextPage, queryEnabled])

  return {
    trucks,
    refetch,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  }
}
